import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Disc3, Music, Loader2, ArrowUpDown, RefreshCcw, Download, Sparkles, Pencil, MoreHorizontal, Share2, Video, HandMetal } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import ShareDialog from '@/components/ShareDialog';
import VideoWallModal from '@/components/VideoWallModal';
import CoverPickerModal from '@/components/CoverPickerModal';
import CoverPreview from '@/components/CoverPreview';
import PassModal from '@/components/PassModal';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import MusicCardCompact from '@/components/MusicCardCompact';
import MusicCardDetail from '@/components/MusicCardDetail';
import CrateSearchBar from '@/components/CrateSearchBar';
import RecheckSuggestionsModal from '@/components/RecheckSuggestionsModal';
import SnapAndSeekButton from '@/components/SnapAndSeekButton';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const EMPTY_FILTERS = { q: '', bpm_min: '', bpm_max: '', key: '', source: '', want: false };

// Crate sort options. The label is what shows in the dropdown; `key` is the
// card field to compare on. We persist the user's choice in localStorage so
// each crate remembers what they last sorted by.
const SORT_OPTIONS = [
  { id: 'recent', label: 'Most recent', field: 'created_at', dir: 'desc' },
  { id: 'oldest', label: 'Oldest first', field: 'created_at', dir: 'asc' },
  { id: 'title-asc', label: 'Title (A → Z)', field: 'title', dir: 'asc' },
  { id: 'title-desc', label: 'Title (Z → A)', field: 'title', dir: 'desc' },
  { id: 'artist-asc', label: 'Artist (A → Z)', field: 'artist', dir: 'asc' },
  { id: 'artist-desc', label: 'Artist (Z → A)', field: 'artist', dir: 'desc' },
  { id: 'bpm-asc', label: 'BPM (slow → fast)', field: 'bpm', dir: 'asc' },
  { id: 'bpm-desc', label: 'BPM (fast → slow)', field: 'bpm', dir: 'desc' },
];

const SORT_STORAGE_KEY = 'groovlens.crateSort';

export default function Album() {
  const { bucket } = useParams();
  const decodedBucket = decodeURIComponent(bucket);
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);
  const [spotifyStatus, setSpotifyStatus] = useState(null);
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistPublic, setPlaylistPublic] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [shareOpen, setShareOpen] = useState(false);
  const [videoWallOpen, setVideoWallOpen] = useState(false);

  // Cover art state — supports all sources (default collage, AI, color,
  // gradient, upload, top_track, emoji, pattern). Backend endpoint
  // `/crates/{bucket}/cover` returns { cover_type, cover_config, cover_path }.
  // This page loads it once on mount and passes it into CoverPickerModal.
  const [coverType, setCoverType] = useState('default_collage');
  const [coverConfig, setCoverConfig] = useState({});
  const [coverPath, setCoverPath] = useState(null);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);
  const [passCrateOpen, setPassCrateOpen] = useState(false);
  // `coverKey` bumps every regenerate so the <img> forces a re-fetch
  // (path already changes, but this avoids any residual SW/browser cache).
  const [coverKey, setCoverKey] = useState(0);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('groovlens_token') : null;

  // Re-check (genre reclassification suggestions) modal state \u2014 scoped to
  // this crate via the `bucket` query param on /cards/reclassify-suggestions.
  const [recheckOpen, setRecheckOpen] = useState(false);
  const [recheckLoading, setRecheckLoading] = useState(false);
  const [recheckSuggestions, setRecheckSuggestions] = useState([]);

  // Sort state — persisted per-user across sessions in localStorage.
  const [sortId, setSortId] = useState(() => {
    try {
      return localStorage.getItem(SORT_STORAGE_KEY) || 'recent';
    } catch (_) { return 'recent'; }
  });
  const activeSort = useMemo(
    () => SORT_OPTIONS.find((o) => o.id === sortId) || SORT_OPTIONS[0],
    [sortId],
  );
  const handleSortChange = (id) => {
    setSortId(id);
    try { localStorage.setItem(SORT_STORAGE_KEY, id); } catch (_) { /* private mode */ }
  };

  // Client-side sorted view — server returns cards in creation order; we
  // re-sort here so the user's choice applies instantly without an extra
  // network round-trip.
  const sortedCards = useMemo(() => {
    if (!cards || cards.length === 0) return [];
    const { field, dir } = activeSort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...cards].sort((a, b) => {
      const av = a?.[field];
      const bv = b?.[field];
      // Push null/undefined to the bottom regardless of direction so missing
      // BPMs / titles don't crowd the top of the list.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * mult;
      }
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * mult;
    });
  }, [cards, activeSort]);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status: 'active', genre: decodedBucket };
      if (filters.q) params.q = filters.q;
      if (filters.bpm_min) params.bpm_min = filters.bpm_min;
      if (filters.bpm_max) params.bpm_max = filters.bpm_max;
      if (filters.key) params.key = filters.key;
      if (filters.source) params.source = filters.source;
      if (filters.want) params.want = true;
      const { data } = await api.get('/cards', { params });
      setCards(data.cards || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [decodedBucket, filters]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  useEffect(() => {
    api.get('/spotify/status').then(({ data }) => setSpotifyStatus(data)).catch(() => {});
  }, []);

  // Fetch the current cover for this crate on mount / bucket change.
  useEffect(() => {
    let cancelled = false;
    api
      .get(`/crates/${encodeURIComponent(decodedBucket)}/cover`)
      .then(({ data }) => {
        if (cancelled) return;
        setCoverPath(data?.cover_path || null);
        setCoverType(data?.cover_type || 'default_collage');
        setCoverConfig(data?.cover_config || {});
      })
      .catch(() => { /* no cover is a totally valid state */ });
    return () => { cancelled = true; };
  }, [decodedBucket]);

  const handleCoverApplied = useCallback((newCover) => {
    setCoverPath(newCover?.cover_path || null);
    setCoverType(newCover?.cover_type || 'default_collage');
    setCoverConfig(newCover?.cover_config || {});
    setCoverKey((k) => k + 1);
  }, []);

  const handleMarkOwned = async (card) => {
    try {
      await api.post(`/cards/${card.id}/owned`);
      toast.success(`Filed as Owned: ${card.title}`);
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleDelete = async (card) => {
    if (!window.confirm('Delete this card from your collection?')) return;
    try {
      await api.delete(`/cards/${card.id}`);
      toast.success('Deleted');
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error('Failed');
    }
  };

  const handleToggleWant = async (card) => {
    // Optimistic update — flip the flag locally so the star badge responds
    // instantly even before the network round-trip completes.
    const prevWant = !!card.want;
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !prevWant } : c)));
    setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !prevWant } : sel));
    try {
      const { data } = await api.post(`/cards/${card.id}/toggle-want`);
      toast.success(data?.want ? 'Added to want list' : 'Removed from want list');
      // Reconcile with server truth in case of collisions.
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: !!data?.want } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: !!data?.want } : sel));
    } catch (e) {
      // Roll back on failure.
      setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, want: prevWant } : c)));
      setSelected((sel) => (sel && sel.id === card.id ? { ...sel, want: prevWant } : sel));
      toast.error(e?.response?.data?.detail || 'Failed to update want list');
    }
  };

  const handleGenreChange = async (card, newBucket) => {
    try {
      await api.patch(`/cards/${card.id}/genre`, { genre_bucket: newBucket });
      toast.success(`Moved to ${newBucket} crate`);
      // The track has left this album view — close modal and refresh.
      setSelected(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to change genre');
    }
  };

  // Run a Re-check scoped to this single crate. We pass `bucket` as a query
  // param so the backend only considers cards currently filed in
  // `decodedBucket`. Three outcomes per spec:
  //   \u2022 0 suggestions       \u2192 "Crate lookin' good!" toast (3s, no interruption)
  //   \u2022 1\u20132 suggestions      \u2192 toast with inline "View N suggestions" action
  //   \u2022 3+ suggestions      \u2192 open the full review modal directly
  const runRecheck = async () => {
    setRecheckLoading(true);
    try {
      const { data } = await api.post('/cards/reclassify-suggestions', null, {
        params: { bucket: decodedBucket },
      });
      const list = data?.suggestions || [];
      const n = list.length;
      if (n === 0) {
        toast.success("\ud83d\udc4d Crate lookin' good!", { duration: 3000 });
        return;
      }
      if (n < 3) {
        toast(
          `${n} ${n === 1 ? 'track' : 'tracks'} in ${decodedBucket} might belong elsewhere`,
          {
            duration: 8000,
            action: {
              label: `View ${n} ${n === 1 ? 'suggestion' : 'suggestions'}`,
              onClick: () => {
                setRecheckSuggestions(list);
                setRecheckOpen(true);
              },
            },
          },
        );
        return;
      }
      setRecheckSuggestions(list);
      setRecheckOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Re-check failed');
    } finally {
      setRecheckLoading(false);
    }
  };

  const openPlaylistDialog = () => {
    if (cards.length === 0) {
      toast.error('Nothing in this crate to send');
      return;
    }
    setPlaylistName(`${decodedBucket} \u00b7 GroovLens`);
    setPlaylistOpen(true);
  };

  const submitCreatePlaylist = async (e) => {
    e.preventDefault();
    if (!playlistName.trim()) {
      toast.error('Give the playlist a name');
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/spotify/playlist/create', {
        name: playlistName.trim(),
        card_ids: cards.map((c) => c.id),
        public: playlistPublic,
        description: `Auto-generated from your ${decodedBucket} crate on GroovLens.`,
      });
      toast.success(`Sent ${data.added} tracks to Spotify`);
      setPlaylistOpen(false);
      if (data.playlist?.external_url) {
        window.open(data.playlist.external_url, '_blank');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  // Per-crate CSV download. Uses the axios `api` client so the auth
  // token + interceptors stay consistent with the rest of the app
  // (the previous native-fetch version was tripping on a stale service
  // worker that returned undefined). Server builds the filename based
  // on the `genre` query param.
  const handleExportCrateCSV = async () => {
    try {
      const resp = await api.get('/cards/export.csv', {
        params: { genre: decodedBucket },
        responseType: 'blob',
      });
      const blob = resp?.data;
      if (!blob) throw new Error('Empty response from server');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().slice(0, 10);
      const safeBucket = decodedBucket.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      a.href = url;
      a.download = `groovlens-${safeBucket}-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${decodedBucket} exported`);
    } catch (e) {
      // Surface the actual server error so any remaining failures are
      // easy to diagnose. Blob-typed errors don't have .response.data
      // unwrapped, so we sniff the status code from the axios error.
      const status = e?.response?.status;
      const detail = status ? `HTTP ${status}` : (e?.message || 'unknown');
      toast.error(`Export failed: ${detail}`);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/crate')}
            aria-label="Back"
            data-testid="album-back-button"
            className="rounded-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight truncate">
              {decodedBucket} crate
            </h1>
            <p className="text-xs text-muted-foreground">
              {cards.length} {cards.length === 1 ? 'pull' : 'pulls'} in this crate
            </p>
          </div>
        </div>
        {cards.length > 0 && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setShareOpen(true)}
              data-testid="album-share-crate"
              className="rounded-xl"
              aria-label={`Share ${decodedBucket} crate`}
            >
              <Share2 className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Share</span>
            </Button>
            <Button
              variant="outline"
              onClick={handleExportCrateCSV}
              data-testid="album-export-csv"
              className="rounded-xl"
              aria-label={`Download ${decodedBucket} crate as text`}
            >
              <Download className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Export as text</span>
            </Button>
            {/* Crate Video Wall — wrapped in the signature animated gradient
                border (`.crate-neon-border`) to signal it as the headline
                action. Inline padding: 2.5px matches the Spotify button's
                2.5px border thickness. The inner button carries the
                `.crate-neon-hover-fill` helper class which paints a subtle
                animated gradient BEHIND the button on hover (35% opacity)
                so the fill feels like an extension of the ring rather than
                a competing surface. */}
            <div className="crate-neon-border rounded-xl" style={{ padding: '2px' }}>
              <Button
                onClick={() => setVideoWallOpen(true)}
                data-testid="album-crate-video-wall"
                variant="ghost"
                className="crate-neon-hover-fill rounded-[9px] bg-card hover:bg-card text-foreground hover:text-white border-0 shadow-none transition-colors duration-150"
                aria-label={`Open ${decodedBucket} crate video wall`}
              >
                <Video className="w-4 h-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Crate Video Wall</span>
              </Button>
            </div>
            {spotifyStatus?.connected && (
              <Button
                onClick={openPlaylistDialog}
                data-testid="album-send-to-spotify"
                variant="outline"
                className="rounded-xl border-[2.5px] border-[#1DB954]/70 text-[#1DB954] bg-transparent hover:bg-[#1DB954]/35 hover:text-white hover:border-[#1DB954] transition-colors duration-150"
              >
                <Music className="w-4 h-4 mr-1.5" /> Send to Spotify
              </Button>
            )}
            <Button
              onClick={() => setPassCrateOpen(true)}
              variant="outline"
              className="rounded-xl border-[2.5px] border-[hsl(var(--primary))]/60 text-[hsl(var(--primary))] bg-transparent hover:bg-[hsl(var(--primary))]/35 hover:text-white hover:border-[hsl(var(--primary))] transition-colors duration-150"
              data-testid="album-pass-crate-button"
              title="Pass this whole crate to another GroovLenser"
            >
              <HandMetal className="w-4 h-4 mr-1.5" /> Pass Crate
            </Button>
          </div>
        )}
      </div>

      {/* Crate cover — unified panel that opens the CoverPickerModal for
          all cover sources (default collage, AI, color, gradient, upload,
          top track art, emoji, pattern). */}
      {cards.length > 0 && (
        <div
          className="mb-4 rounded-2xl border border-border bg-card p-3 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center"
          data-testid="crate-cover-panel"
        >
          <div className="w-full sm:w-32 aspect-square rounded-xl overflow-hidden border border-border/60 shrink-0 relative">
            <CoverPreview
              bucket={decodedBucket}
              art={(cards || []).map((c) => c.album_art_url).filter(Boolean).slice(0, 4)}
              coverType={coverType}
              coverConfig={coverConfig}
              coverPath={coverPath}
              cacheKey={coverKey}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
              <h3 className="font-display text-sm font-semibold">Crate cover</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
              {coverType === 'default_collage'
                ? `Currently showing the default GroovLens collage — a 2×2 sample of the most recent tracks in ${decodedBucket}. Personalize it with a color, gradient, uploaded image, or AI.`
                : coverType === 'ai'
                ? `Custom AI-generated artwork for your ${decodedBucket} crate. Change cover to regenerate, upload your own, or pick a color / gradient.`
                : coverType === 'upload'
                ? `Your uploaded image is set as this crate's cover.`
                : coverType === 'color'
                ? `Solid color cover applied.`
                : coverType === 'gradient'
                ? `Gradient cover applied.`
                : coverType === 'emoji'
                ? `Emoji cover applied.`
                : coverType === 'top_track'
                ? `Using top track album art as this crate's cover.`
                : `Pattern cover applied.`}
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setCoverPickerOpen(true)}
                className="rounded-lg h-8 text-xs"
                data-testid="crate-cover-change-button"
              >
                <Pencil className="w-3.5 h-3.5 mr-1.5" />
                Change cover
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cover Picker modal — shared with the collection grid pencil overlay. */}
      <CoverPickerModal
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        bucket={decodedBucket}
        art={(cards || []).map((c) => c.album_art_url).filter(Boolean).slice(0, 4)}
        initialCover={{
          cover_type: coverType,
          cover_config: coverConfig,
          cover_path: coverPath,
        }}
        onApplied={handleCoverApplied}
      />

      {/* Pass this whole crate to another GroovLenser (in-person QR handoff) */}
      <PassModal
        open={passCrateOpen}
        onClose={() => setPassCrateOpen(false)}
        bucket={decodedBucket}
        defaultMode="crate"
      />

      {/* Re-check this crate \u2014 scoped suggestion review */}
      {cards.length > 0 && (
        <div className="mb-3 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={runRecheck}
            disabled={recheckLoading}
            className="rounded-xl"
            data-testid="album-recheck-button"
            title={`Re-check every track in ${decodedBucket} against current tags`}
          >
            {recheckLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCcw className="w-4 h-4 mr-2" />
            )}
            Re-check this crate
          </Button>
        </div>
      )}

      <div className="mb-4 max-w-4xl mx-auto flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="flex-1 min-w-0">
          <CrateSearchBar
            filters={filters}
            onChange={setFilters}
            placeholder={`Search this ${decodedBucket} crate…`}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="rounded-xl h-10 px-3 shrink-0 justify-start sm:justify-center gap-1.5"
              data-testid="crate-sort-trigger"
              aria-label="Sort tracks"
            >
              <ArrowUpDown className="w-3.5 h-3.5" />
              <span className="text-xs truncate">{activeSort.label}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48" data-testid="crate-sort-menu">
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuItem
                key={opt.id}
                onSelect={(e) => {
                  e.preventDefault();
                  handleSortChange(opt.id);
                }}
                className={`text-xs cursor-pointer ${opt.id === sortId ? 'text-[hsl(var(--primary))] font-medium' : ''}`}
                data-testid={`crate-sort-option-${opt.id}`}
              >
                {opt.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={`skeleton-${i}`} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : cards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="album-empty-state">
          <Disc3 className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          {filters.q || filters.bpm_min || filters.bpm_max || filters.key || filters.source || filters.want ? (
            <>
              <h3 className="font-display text-lg font-semibold">No matches</h3>
              <p className="text-sm text-muted-foreground">Try clearing some filters.</p>
              <Button
                variant="outline"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="mt-4 rounded-xl"
                data-testid="album-clear-filters"
              >
                Clear filters
              </Button>
            </>
          ) : (
            <>
              <h3 className="font-display text-lg font-semibold">Empty crate</h3>
              <p className="text-sm text-muted-foreground">Scan more screenshots to fill it.</p>
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap">
                <Link to="/upload">
                  <Button className="rounded-xl border-2 border-[#FFB24A] !bg-transparent text-[#FFB24A] hover:!bg-[#FFB24A]/35 hover:text-white active:!bg-[#FFB24A]/35 active:text-white focus-visible:!bg-[#FFB24A]/35 focus-visible:text-white focus-visible:ring-[#FFB24A] hover:shadow-[0_0_24px_-8px_#FFB24A] transition-colors duration-150">
                    Scan Screenshots
                  </Button>
                </Link>
                <SnapAndSeekButton testid="album-empty-snap-and-seek" />
              </div>
            </>
          )}
        </div>
      ) : (
        <ul className="space-y-2 max-w-4xl mx-auto" data-testid="album-cards-list">
          {sortedCards.map((card) => (
            <li key={card.id}>
              <MusicCardCompact
                card={card}
                onOpen={setSelected}
                onMarkOwned={handleMarkOwned}
                onDelete={handleDelete}
                isPreviewing={previewingId === card.id}
                onPreviewToggle={() => setPreviewingId((id) => (id === card.id ? null : card.id))}
              />
            </li>
          ))}
        </ul>
      )}

      <MusicCardDetail
        card={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onMarkOwned={handleMarkOwned}
        onDelete={handleDelete}
        onGenreChange={handleGenreChange}
        onToggleWant={handleToggleWant}
        // Deck navigation — lets the user browse this crate one card at a
        // time with arrows, keyboard, or swipe, without closing the modal.
        deck={sortedCards}
        deckIndex={selected ? sortedCards.findIndex((c) => c.id === selected.id) : -1}
        onNavigate={(newCard) => setSelected(newCard)}
      />

      {/* Share this whole crate (login-gated link between GroovLens users) */}
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        kind="crate"
        target={{
          title: `${decodedBucket} crate`,
          subtitle: `${cards.length} ${cards.length === 1 ? 'pull' : 'pulls'}`,
          image: cards[0]?.album_art_url,
        }}
        payload={{ bucket: decodedBucket }}
      />

      {/* Crate-mode Video Wall \u2014 sends the sorted track list so the
          sidebar playlist matches on-page ordering. Sidebar entries lazy-
          load their YouTube payloads on click to stay under the 10k/day
          Data API quota. */}
      <VideoWallModal
        open={videoWallOpen}
        onOpenChange={setVideoWallOpen}
        mode="crate"
        tracks={sortedCards}
        crateName={decodedBucket}
      />

      {/* Re-check (genre reclassification suggestions) modal \u2014 scoped to
          this crate. We refresh the list when it closes since accepted
          moves will have left the bucket. */}
      <RecheckSuggestionsModal
        open={recheckOpen}
        onOpenChange={setRecheckOpen}
        scope="crate"
        scopeLabel={decodedBucket}
        suggestions={recheckSuggestions}
        onAfterAction={() => fetch()}
      />

      {/* Send to Spotify Playlist dialog */}
      <Dialog open={playlistOpen} onOpenChange={setPlaylistOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="playlist-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Music className="w-5 h-5 text-[#1DB954]" /> Send to Spotify
            </DialogTitle>
            <DialogDescription>
              Create a new playlist on your Spotify account with all {cards.length} tracks from this crate.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreatePlaylist} className="space-y-3">
            <div>
              <Label htmlFor="playlist-name">Playlist name</Label>
              <Input
                id="playlist-name"
                value={playlistName}
                onChange={(e) => setPlaylistName(e.target.value)}
                placeholder={`${decodedBucket} · GroovLens`}
                data-testid="playlist-name-input"
                required
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground select-none">
              <input
                type="checkbox"
                checked={playlistPublic}
                onChange={(e) => setPlaylistPublic(e.target.checked)}
                data-testid="playlist-public-checkbox"
              />
              Make public
            </label>
            <DialogFooter className="gap-2">
              <Button type="button" variant="ghost" onClick={() => setPlaylistOpen(false)} className="rounded-lg">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={creating}
                className="rounded-lg bg-[#1DB954] hover:bg-[#1ed760] text-black"
                data-testid="playlist-create-button"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
                  </>
                ) : (
                  <>Create playlist</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
